/**
 * №22.5 (F9) — a conversation opens at its NEWEST message, and stays there.
 *
 * The thread rendered top-down and never scrolled, so opening a run put you at the
 * oldest turn and every new message landed off-screen below the fold. A chat that
 * shows you the beginning is showing you the least useful end of itself.
 *
 * The rule, and the second half matters as much as the first:
 *
 *   - **On open, jump to the bottom.** Instantly, not smoothly — a smooth scroll from
 *     the top of a long thread is a several-hundred-millisecond animation of content
 *     nobody asked to see.
 *   - **On new content, follow ONLY if the reader was already at the bottom.** Someone
 *     scrolled up is READING something. Yanking them to the newest line because a run
 *     finished is the same class of rudeness as a page that reflows under your cursor,
 *     and it is worse here because what they scrolled up to read is usually the
 *     evidence for the decision they are about to make.
 *
 * Attaches to the nearest scrollable ancestor rather than taking a container prop, so
 * one hook serves every host the thread has (the docked panel's `.copanel__stream`,
 * the full-page run subject, the rail) without any of them knowing about it.
 */
import { useEffect, useRef } from "react";

/** How close to the bottom still counts as "at the bottom" (px). Roughly one line. */
const NEAR_BOTTOM_PX = 80;

function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const style = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
}

const isNearBottom = (el: HTMLElement): boolean =>
  el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;

/**
 * @param signal  Changes whenever the thread's content changes (message count, the
 *                running flag, the last message's id). Drives the follow.
 * @param ready   False while the thread is still loading — the first jump waits for
 *                real content, or it lands on an empty box and never re-fires.
 */
export function useStickToBottom(signal: unknown, ready = true) {
  const endRef = useRef<HTMLDivElement | null>(null);
  const didFirstJump = useRef(false);
  // Whether the reader was at the bottom BEFORE this content change — sampled on
  // scroll, because by the time the new content has rendered the measurement is
  // already contaminated by it.
  const wasAtBottom = useRef(true);

  useEffect(() => {
    const parent = scrollParentOf(endRef.current);
    if (!parent) return;
    const onScroll = () => { wasAtBottom.current = isNearBottom(parent); };
    parent.addEventListener("scroll", onScroll, { passive: true });
    return () => parent.removeEventListener("scroll", onScroll);
  }, [ready]);

  useEffect(() => {
    const end = endRef.current;
    const parent = scrollParentOf(end);
    if (!end || !parent || !ready) return;

    if (!didFirstJump.current) {
      didFirstJump.current = true;
      wasAtBottom.current = true;
      parent.scrollTop = parent.scrollHeight;   // instant, on open
      return;
    }
    if (wasAtBottom.current) end.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [signal, ready]);

  // Late-arriving height — markdown reflow, a disclosure opening, an avatar loading —
  // moves the bottom after the effect above has run. Follow it, still only when the
  // reader was already there.
  useEffect(() => {
    const end = endRef.current;
    const parent = scrollParentOf(end);
    if (!end || !parent || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (didFirstJump.current && wasAtBottom.current) parent.scrollTop = parent.scrollHeight;
    });
    ro.observe(parent);
    for (const child of Array.from(parent.children)) ro.observe(child);
    return () => ro.disconnect();
  }, [ready]);

  /** Render this as the LAST child of the thread. */
  return endRef;
}
